import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches a render error in one section so it doesn't blank the whole page (React unmounts everything
 * above an uncaught error). Give it a `key` that changes with the section, so moving on starts afresh.
 */
export class SectionBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Section failed to render', error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex flex-col items-start gap-3 rounded-[20px] bg-panel p-6" role="alert">
        <p className="m-0 text-[15px] font-semibold">This section couldn't be shown.</p>
        <p className="m-0 text-sm text-muted">The rest of the page still works. Try again, or reload if a new version was just deployed.</p>
        <div className="flex gap-2">
          <button type="button" onClick={() => this.setState({ failed: false })} className="h-10 rounded-full bg-fg px-4 text-sm font-medium text-bg">
            Try again
          </button>
          <button type="button" onClick={() => window.location.reload()} className="h-10 rounded-full bg-[#ebebeb] px-4 text-sm font-medium text-fg hover:bg-[#e2e2e2]">
            Reload
          </button>
        </div>
      </div>
    );
  }
}
